import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { useScreenTour } from "@/hooks/useScreenTour";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const money = (n, currency = "USD") => (n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 })} ${currency}`);

const PERIOD_OPTIONS = [
  { value: "este_mes", label: "Este mes" },
  { value: "mes_pasado", label: "Mes pasado" },
  { value: "ultimos_3_meses", label: "Últimos 3 meses" },
  { value: "anio", label: "Este año" },
  { value: "custom", label: "Rango personalizado" },
];

function Kpi({ label, usd, mxn, testId, tone = "default" }) {
  const TONES = { default: "text-foreground", danger: "text-red-600", success: "text-primary" };
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`mt-1 font-mono text-xl font-bold ${TONES[tone]}`}>{money(usd)}</p>
        {mxn != null && <p className="mt-0.5 font-mono text-xs text-muted-foreground">{money(mxn, "MXN")}</p>}
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { can } = useAuth();
  const { companyFilter } = usePrefs();
  const navigate = useNavigate();
  useScreenTour("dashboard");
  const canVerGanancia = can("dashboard", "ver_ganancia");
  const [period, setPeriod] = useState("este_mes");
  const [tipoViaje, setTipoViaje] = useState("all");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [summary, setSummary] = useState(null);
  const [ganMes, setGanMes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (period === "custom" && (!fechaDesde || !fechaHasta)) return;
    setLoading(true);
    try {
      const params = { period, company_id: companyFilter, tipo_viaje: tipoViaje };
      if (period === "custom") { params.fecha_desde = fechaDesde; params.fecha_hasta = fechaHasta; }
      const [{ data: s }, { data: g }] = await Promise.all([
        api.get("/dashboard/summary", { params }),
        api.get("/dashboard/ganancia-por-mes", { params: { company_id: companyFilter, tipo_viaje: tipoViaje } }),
      ]);
      setSummary(s);
      setGanMes(g);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [period, companyFilter, tipoViaje, fechaDesde, fechaHasta]);

  useEffect(() => { load(); }, [load]);

  if (loading && !summary) return <div className="text-sm text-muted-foreground">Cargando...</div>;
  if (!summary) return null;
  const k = summary.kpis;

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Resumen" subtitle="El dinero de un vistazo: cobrado, por cobrar, vencido y ganancia." />

      <div className="mb-5 flex flex-wrap items-end gap-2">
        <div data-tour="dashboard-period">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48" data-testid="dashboard-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {period === "custom" && (
          <>
            <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} data-testid="dashboard-fecha-desde" />
            <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} data-testid="dashboard-fecha-hasta" />
          </>
        )}
        <Select value={tipoViaje} onValueChange={setTipoViaje}>
          <SelectTrigger className="w-48" data-testid="dashboard-tipo-viaje-select"><SelectValue placeholder="Tipo de viaje" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="nacional_mx">Solo nacional MX</SelectItem>
            <SelectItem value="nacional_us">Solo nacional US</SelectItem>
            <SelectItem value="transfronterizo">Solo transfronterizo</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-muted-foreground">{summary.periodo.desde} → {summary.periodo.hasta}</span>
      </div>

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-tour="dashboard-kpis">
        <Kpi label="Cobrado en el período" usd={k.total_cobrado_usd} mxn={k.total_cobrado_mxn} testId="kpi-cobrado" tone="success" />
        <Kpi label="Pendiente de cobro" usd={k.total_pendiente_cobro_usd} mxn={k.total_pendiente_cobro_mxn} testId="kpi-pendiente-cobro" />
        <Kpi label="De eso, vencido" usd={k.total_vencido_usd} mxn={k.total_vencido_mxn} testId="kpi-vencido" tone="danger" />
        <Kpi label="Pagado a carriers" usd={k.total_pagado_carriers_usd} mxn={k.total_pagado_carriers_mxn} testId="kpi-pagado-carriers" />
        <Kpi label="Pendiente pago a carriers" usd={k.total_pendiente_pago_carriers_usd} mxn={k.total_pendiente_pago_carriers_mxn} testId="kpi-pendiente-pago-carriers" />
        {canVerGanancia && <Kpi label="Ganancia neta del período" usd={k.ganancia_neta_usd} mxn={k.ganancia_neta_mxn} testId="kpi-ganancia-neta" tone="success" />}
        {canVerGanancia && (
          <Card data-testid="kpi-margen-promedio">
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Margen promedio</p>
              <p className="mt-1 font-mono text-xl font-bold">{k.margen_promedio_pct?.toFixed?.(1) ?? k.margen_promedio_pct}%</p>
            </CardContent>
          </Card>
        )}
        <Card data-testid="kpi-loads-activos">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Loads activos</p>
            <p className="mt-1 font-mono text-xl font-bold">{k.loads_activos_count}</p>
          </CardContent>
        </Card>
        <Card
          data-testid="kpi-sin-fecha-delivery"
          data-tour="dashboard-sin-fecha"
          className="cursor-pointer transition-colors hover:bg-amber-50"
          onClick={() => navigate("/loads?sin_fecha_delivery=true")}
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Viajes sin fecha de entrega</p>
            <p className="mt-1 font-mono text-xl font-bold text-amber-600">{k.sin_fecha_delivery_count ?? 0}</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Vencimiento estimado desde el despacho — verificar</p>
          </CardContent>
        </Card>
      </div>

      {canVerGanancia && (
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card data-testid="chart-ganancia-mensual">
            <CardHeader><CardTitle className="font-heading text-base">Ganancia neta por mes (últimos 6 meses)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={ganMes}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Bar dataKey="ganancia_neta_usd" fill="#16a34a" radius={[4, 4, 0, 0]} name="Ganancia USD" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card data-testid="chart-comparativo-companias">
            <CardHeader><CardTitle className="font-heading text-base">Trébol vs Álvarez Trucking</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={summary.comparativo_companias}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="company_nombre" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="facturacion_usd" fill="#0ea5e9" radius={[4, 4, 0, 0]} name="Facturación" />
                  <Bar dataKey="ganancia_usd" fill="#16a34a" radius={[4, 4, 0, 0]} name="Ganancia" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {!canVerGanancia && (
        <Card className="mb-6" data-testid="chart-comparativo-companias-reducido">
          <CardHeader><CardTitle className="font-heading text-base">Loads por compañía</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {summary.comparativo_companias.map((c) => (
                <div key={c.company_nombre} className="rounded-md border border-border p-3">
                  <p className="text-sm font-medium">{c.company_nombre}</p>
                  <p className="font-mono text-lg">{c.loads_count} loads</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card data-testid="table-cobros-vencidos">
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><AlertTriangle className="h-4 w-4 text-red-600" /> Cobros vencidos</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Cliente</TableHead><TableHead>Load</TableHead><TableHead className="text-right">Monto</TableHead><TableHead className="text-right">Días</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {summary.cobros_vencidos.map((c) => (
                  <TableRow key={c.load_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/loads/${c.load_id}`)} data-testid={`cobro-vencido-row-${c.numero_load}`}>
                    <TableCell className="text-sm">{c.client_nombre}</TableCell>
                    <TableCell className="font-mono text-xs">{c.numero_load}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{money(c.monto_usd)}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${c.semaforo === "critico" ? "text-red-700 font-bold" : "text-orange-600"}`}>{c.dias}d</TableCell>
                  </TableRow>
                ))}
                {summary.cobros_vencidos.length === 0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground">Sin cobros vencidos.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {canVerGanancia && (
          <Card data-testid="table-top-clientes">
            <CardHeader><CardTitle className="font-heading text-base">Top 10 clientes por ganancia neta</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead className="text-right">Ganancia</TableHead></TableRow></TableHeader>
                <TableBody>
                  {summary.top_clientes.map((c) => (
                    <TableRow key={c.client_nombre}><TableCell className="text-sm">{c.client_nombre}</TableCell><TableCell className="text-right font-mono text-xs">{money(c.ganancia_neta_usd)}</TableCell></TableRow>
                  ))}
                  {summary.top_clientes.length === 0 && <TableRow><TableCell colSpan={2} className="py-6 text-center text-muted-foreground">Sin datos.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {canVerGanancia && (
          <Card data-testid="table-top-carriers">
            <CardHeader><CardTitle className="font-heading text-base">Top 10 carriers por costo</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Carrier</TableHead><TableHead className="text-right">Costo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {summary.top_carriers.map((c) => (
                    <TableRow key={c.carrier_nombre}><TableCell className="text-sm">{c.carrier_nombre}</TableCell><TableCell className="text-right font-mono text-xs">{money(c.costo_total_usd)}</TableCell></TableRow>
                  ))}
                  {summary.top_carriers.length === 0 && <TableRow><TableCell colSpan={2} className="py-6 text-center text-muted-foreground">Sin datos.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {canVerGanancia && (
          <Card data-testid="table-fuera-de-tarifa">
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-base"><AlertTriangle className="h-4 w-4 text-orange-500" /> Loads fuera de tarifa</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Load</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Margen result.</TableHead><TableHead className="text-right">Objetivo</TableHead></TableRow></TableHeader>
                <TableBody>
                  {summary.fuera_de_tarifa.map((f) => (
                    <TableRow key={f.load_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/loads/${f.load_id}`)} data-testid={`fuera-tarifa-row-${f.numero_load}`}>
                      <TableCell className="font-mono text-xs">{f.numero_load}</TableCell>
                      <TableCell className="text-sm">{f.client_nombre}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{f.margen_resultante_pct}%</TableCell>
                      <TableCell className="text-right font-mono text-xs">{f.margen_objetivo_pct}%</TableCell>
                    </TableRow>
                  ))}
                  {summary.fuera_de_tarifa.length === 0 && <TableRow><TableCell colSpan={4} className="py-6 text-center text-muted-foreground">Sin loads fuera de tarifa.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
